import React, { type JSXElementConstructor } from 'react';
import _ from 'lodash';
import { MENU_ITEMS_TO_CREATE } from './consts';
import { BasePublicApi } from '#packages/apilib';
import type { Scope } from './previewerEntryPoint';
import type {
  DocumentServicesObject,
  ColorPalette,
  SerializedCompStructure,
  SerializedPageStructure,
  CompStructure,
  PagesStructure,
  PageRef,
  CompRef,
  MiniSiteManager,
  MenuObject,
  ViewModes,
} from '@wix/document-services-types';
import { PreviewerItem } from './components/previewerItem';
import { ErrorReporter } from '@wix/editor-error-reporter';
import { Preview } from './components/preview';
import { IframePreview } from './components/iframePreview';
import {
  adjustHeaderAndFooterIfNeeded,
  getPreviewerEffectsMapping,
  setUsesNewAnimations,
} from './previewerUtils';

import type {
  CreatePageSSRComponentsParams,
  CreatePreviewComponentsParams,
  CreatePreviewParams,
  EnrichedCSMDef,
  PagePreviewData,
  PageSSRComponentDefinition,
  PreviewData,
  SiteTheme,
} from './types';

let isAlreadyInitiated = false;
export const initSetup = ({ ds, setHostReact }: MiniSiteManager) => {
  if (isAlreadyInitiated) return;
  isAlreadyInitiated = true;
  setHostReact(React);

  ds.pages.add(`componentsPageContainer-${Date.now()}`, undefined, false);

  setUsesNewAnimations(ds);

  MENU_ITEMS_TO_CREATE.forEach((menuType: string) => {
    if (ds.menu.getById(menuType)) {
      return;
    }
    ds.menu.create(
      {
        items: [],
        syncWithPages: false,
        type: 'CustomMenu',
        menuType,
      },
      // TODO: remove when bump ds-types
      // @ts-expect-error
      menuType,
    );
  });
};

export async function init(scope: Scope) {
  const { store } = scope;
  const previewFrame = window.document.querySelector('#preview');

  if (previewFrame) {
    if (!(previewFrame as any).contentWindow.miniSiteManager) {
      console.log('this dm version has no miniSiteManager');
      return;
    }

    await (previewFrame as any).contentWindow.miniSiteManager.waitForLoad();
    const miniSiteManager = (previewFrame as any).contentWindow.miniSiteManager;
    if (!miniSiteManager.ds) {
      ErrorReporter.captureMessage('error loading ds to miniSiteManager');
      return;
    }

    store.setMiniSiteManager(miniSiteManager);
    initSetup(miniSiteManager);
  }
}

function updateTheme(
  miniDs: DocumentServicesObject,
  customSiteTheme: SiteTheme,
) {
  const miniSiteDsThemeApi = miniDs.theme;

  const newColors = customSiteTheme?.colors;
  miniSiteDsThemeApi.colors.update(newColors);

  Object.entries(customSiteTheme?.styles)
    .filter(([_, styleData]) => styleData?.styleType === 'system')
    .forEach(([styleId, styleValue]) => {
      miniSiteDsThemeApi.styles.update(styleId, styleValue);
    });

  const newTextThemes = customSiteTheme?.textThemes;
  miniSiteDsThemeApi.textThemes.update(newTextThemes);
}

const findMainMenuItem = (menuObject: MenuObject) =>
  menuObject.getAll().find(({ id }) => id === 'CUSTOM_MAIN_MENU');

const updateMainMenuItems = (
  miniDS: DocumentServicesObject,
  menuItemsNames: string[],
  homePageName: string,
) => {
  const miniSiteDsMenuApi = miniDS.menu;
  const mainMenuItem = findMainMenuItem(miniSiteDsMenuApi);
  if (mainMenuItem) {
    const allMainMenuItemsLabels = mainMenuItem.items.map(({ label }) => label);
    menuItemsNames.forEach((menuItemName) => {
      if (!allMainMenuItemsLabels.includes(menuItemName))
        miniSiteDsMenuApi.addItem(mainMenuItem.id, { label: menuItemName });
    });
    allMainMenuItemsLabels.forEach((menuItemLabel) => {
      if (
        !menuItemsNames.includes(menuItemLabel) &&
        menuItemLabel !== homePageName
      ) {
        const itemToRemove = mainMenuItem.items.find(
          (item) => item.label === menuItemLabel,
        );
        miniSiteDsMenuApi.removeItem(mainMenuItem.id, itemToRemove.id);
      }
    });
  }
};

const updateHomePageName = (
  miniDS: DocumentServicesObject,
  homePageName: string,
) => {
  const miniSiteDsMenuApi = miniDS.menu;
  const mainMenuItem = findMainMenuItem(miniSiteDsMenuApi);
  if (mainMenuItem) {
    miniSiteDsMenuApi.updateItem(mainMenuItem.id, mainMenuItem.items[0].id, {
      label: homePageName,
    });
  }
};

const getHomePageName = (miniDS: DocumentServicesObject) => {
  const miniSiteDsMenuApi = miniDS.menu;
  return findMainMenuItem(miniSiteDsMenuApi)?.items[0].label;
};

const addPage = (
  miniDs: DocumentServicesObject,
  pageStructure: PagesStructure,
) => {
  delete pageStructure.data.pageUriSEO;
  const pageRef: PageRef = miniDs.pages.add(
    `page-thumbnail-${Date.now()}`,
    pageStructure,
    false,
  );

  return pageRef;
};

const addAsPage = (
  miniDs: DocumentServicesObject,
  compStructure: CompStructure,
  isMobile?: boolean,
  compsOldToNewIdsMap?: Record<string, string>,
) => {
  const pageRef: PageRef = miniDs.pages.add(
    `thumbnail-${Date.now()}`,
    undefined,
    false,
  );
  compStructure.layout.y = 0;
  const ref = isMobile ? miniDs.components.getMobileRef(pageRef) : pageRef;
  miniDs.components.add(
    ref,
    compStructure,
    '',
    null,
    // @ts-expect-error
    compsOldToNewIdsMap,
  );
  return pageRef;
};

async function createThumbnailComponentFromThumbnailId(
  createPreviewElements: MiniSiteManager['createPreviewElements'],
  thumbnailPointer: PageRef | CompRef,
  sourceTemplateId?: string,
  enrichedCSM?: EnrichedCSMDef,
): Promise<JSXElementConstructor<{ onReady: Function }>> {
  // TODO: handle as single when have right API in DM, currently enforcing on editor side
  const [thumbnailComponent] = await createPreviewElements([thumbnailPointer], {
    applicationCSMTemplateID: sourceTemplateId,
    enrichedCSM,
  });
  return thumbnailComponent;
}

async function addComponentsForPreview(
  miniDs: DocumentServicesObject,
  structure: SerializedCompStructure | SerializedPageStructure,
  compsOldToNewIdsMap?: Record<string, string>,
  isMobile?: boolean,
): Promise<PageRef> {
  const clonedStructure = _.cloneDeep(structure);
  const isPage = structure.type === 'Page';

  fixStructureLayout(clonedStructure);
  let pageRef;

  if (isPage) {
    pageRef = addPage(miniDs, clonedStructure as PagesStructure);
  } else {
    pageRef = addAsPage(
      miniDs,
      clonedStructure as CompStructure,
      isMobile,
      compsOldToNewIdsMap,
    );
  }

  await miniDs.waitForChangesAppliedAsync();
  return pageRef;
}

// When component has fixedPosition===true -> component doesn't take up space in
// ThumbnailPreview & wrapper divs receive incorrect height
const fixStructureLayout = (
  structure: SerializedCompStructure | SerializedPageStructure,
) => {
  structure.layout.fixedPosition = false;
};

async function updatePreviewInDS(
  miniDs: DocumentServicesObject,
  viewMode: string,
  scaleValue?: number,
  customMenuItemsNames?: string[],
  customSiteTheme?: SiteTheme,
  customHomePageName?: string,
  enableVideoPlaying?: boolean,
) {
  updateTheme(miniDs, customSiteTheme);
  if (customHomePageName) {
    updateHomePageName(miniDs, customHomePageName);
  }
  if (customMenuItemsNames) {
    updateMainMenuItems(miniDs, customMenuItemsNames, getHomePageName(miniDs));
  }

  miniDs.documentMode.enablePlaying(Boolean(enableVideoPlaying));
  miniDs.documentMode.setImageTargetScale(scaleValue);

  miniDs.viewMode.set(viewMode);
  await miniDs.waitForChangesAppliedAsync();
}

const createPreviewerPageSSR = async (
  miniDs: DocumentServicesObject,
  previewData: PagePreviewData,
  enrichedCSM: EnrichedCSMDef,
): Promise<PageSSRComponentDefinition> => {
  const {
    pageStructure,
    compsOldToNewIdsMap,
    headerStructure,
    footerStructure,
  } = previewData;
  const { pageRef, html, effectsMapping } = await miniDs.transactions.run(
    async () => {
      const pageRef = await addComponentsForPreview(
        miniDs,
        pageStructure,
        compsOldToNewIdsMap,
        false,
      );
      await adjustHeaderAndFooterIfNeeded(
        miniDs,
        pageStructure,
        headerStructure,
        footerStructure,
      );
      const html = await miniDs.miniSite.createPreviewElementsHTML(pageRef, {
        enrichedCSM,
      });
      const effectsMapping = getPreviewerEffectsMapping(miniDs, [
        pageRef,
        miniDs.siteSegments.getHeader(),
        miniDs.siteSegments.getFooter(),
      ]);
      miniDs.pages.remove(pageRef.id);
      return {
        pageRef,
        html,
        effectsMapping,
      };
    },
  );
  if (!pageRef) {
    const error = new Error('PreviewerAPI Failed to create thumbnailPointer');
    ErrorReporter.captureException(error, {
      tags: { previewerApiCreatePreviewComponent: true },
      extra: {
        previewData,
      },
    });
    throw error;
  }

  return { pageRef, html, effectsMapping };
};

async function createThumbnail(
  miniDs: DocumentServicesObject,
  previewData: PreviewData,
  sourceTemplateId: string,
  isMobile: boolean,
  createPreviewElements: MiniSiteManager['createPreviewElements'],
  viewMode: string,
  customMenuItemsNames: string[],
  enrichedCSM?: EnrichedCSMDef,
  scaleValue?: number,
  customSiteTheme?: SiteTheme,
  customHomePageName?: string,
  enableVideoPlaying?: boolean,
) {
  const { structure, compsOldToNewIdsMap } = previewData;
  const { thumbnailPointer, thumbnailComponent, effectsMapping } =
    await miniDs.transactions.run(async () => {
      const pageRef = await addComponentsForPreview(
        miniDs,
        structure as SerializedCompStructure,
        compsOldToNewIdsMap,
        isMobile,
      );
      const rootPointer = { id: pageRef.id, type: viewMode as keyof ViewModes };

      const thumbnailComponent = await createThumbnailComponentFromThumbnailId(
        createPreviewElements,
        rootPointer,
        sourceTemplateId,
        enrichedCSM,
      );
      const effectsMapping = getPreviewerEffectsMapping(miniDs, [rootPointer]);
      if (miniDs.utils.isPage(rootPointer)) {
        miniDs.pages.remove(rootPointer.id);
      }

      return {
        thumbnailPointer: rootPointer,
        thumbnailComponent,
        effectsMapping,
      };
    });
  if (!thumbnailPointer) {
    const error = new Error('PreviewerAPI Failed to create thumbnailPointer');
    ErrorReporter.captureException(error, {
      tags: { previewerApiCreatePreviewComponent: true },
      extra: {
        previewData,
        scaleValue,
        customSiteTheme,
        sourceTemplateId,
        isMobile,
        customHomePageName,
        customMenuItemsNames,
        enableVideoPlaying,
      },
    });
    throw error;
  }

  return { thumbnailPointer, thumbnailComponent, effectsMapping };
}

export async function unstable_createPageSSRComponents(
  miniDs: DocumentServicesObject,
  {
    previewData,
    scaleValue = 1,
    customSiteTheme,
    customHomePageName,
    displayConfig,
    customMenuItemsNames,
    onReady,
    isMobile,
    enrichedCSM,
  }: CreatePageSSRComponentsParams,
): Promise<React.ReactElement> {
  const viewMode = isMobile
    ? miniDs.viewMode.VIEW_MODES.MOBILE
    : miniDs.viewMode.VIEW_MODES.DESKTOP;

  await updatePreviewInDS(
    miniDs,
    viewMode,
    scaleValue,
    customMenuItemsNames,
    customSiteTheme,
    customHomePageName,
  );

  const { html, effectsMapping } = await createPreviewerPageSSR(
    miniDs,
    previewData,
    enrichedCSM,
  );

  return (
    <IframePreview
      html={html}
      effectsMapping={effectsMapping}
      baseWidth={displayConfig.baseWidth}
      displayWidth={displayConfig.displayWidth}
      onReady={onReady}
    />
  );
}

export async function createPreview(
  miniDs: DocumentServicesObject,
  createPreviewElements: MiniSiteManager['createPreviewElements'],
  {
    enrichedCSM,
    previews,
    scaleValue = 1,
    customSiteTheme,
    customHomePageName,
    enableVideoPlaying,
    displayConfig,
    customMenuItemsNames,
    onReady,
    isMobile,
  }: CreatePreviewParams,
): Promise<React.ReactElement> {
  const viewMode = isMobile
    ? miniDs.viewMode.VIEW_MODES.MOBILE
    : miniDs.viewMode.VIEW_MODES.DESKTOP;

  await updatePreviewInDS(
    miniDs,
    viewMode,
    scaleValue,
    customMenuItemsNames,
    customSiteTheme,
    customHomePageName,
    enableVideoPlaying,
  );

  const previewerThumbnailData = await Promise.all(
    previews.map(
      async ({ previewData, sourceTemplateId, title, previewIndicator }) => {
        const thumbnailData = await createThumbnail(
          miniDs,
          previewData,
          sourceTemplateId,
          isMobile,
          createPreviewElements,
          viewMode,
          customMenuItemsNames,
          enrichedCSM,
          scaleValue,
          customSiteTheme,
          customHomePageName,
          enableVideoPlaying,
        );

        return { title, previewIndicator, ...thumbnailData };
      },
    ),
  );

  return (
    <Preview
      previewItems={previewerThumbnailData.map((previewerItem) => {
        return {
          ThumbnailComponent: previewerItem.thumbnailComponent,
          effectsMapping: previewerItem.effectsMapping,
          title: previewerItem.title,
          previewIndicator: previewerItem.previewIndicator,
        };
      })}
      displayConfig={displayConfig}
      onReady={onReady}
    />
  );
}

export async function createPreviewComponents(
  miniDs: DocumentServicesObject,
  createPreviewElements: MiniSiteManager['createPreviewElements'],
  {
    previewData,
    scaleValue = 1,
    enrichedCSM,
    customSiteTheme,
    sourceTemplateId,
    isMobile = false,
    customHomePageName,
    customMenuItemsNames,
    enableVideoPlaying = true,
    onReady,
    enablePreviewAnimationsPromise = Promise.resolve(),
  }: CreatePreviewComponentsParams,
): Promise<React.ReactElement> {
  const viewMode = isMobile
    ? miniDs.viewMode.VIEW_MODES.MOBILE
    : miniDs.viewMode.VIEW_MODES.DESKTOP;

  updatePreviewInDS(
    miniDs,
    viewMode,
    scaleValue,
    customMenuItemsNames,
    customSiteTheme,
    customHomePageName,
    enableVideoPlaying,
  );

  const { thumbnailPointer, thumbnailComponent } = await createThumbnail(
    miniDs,
    previewData,
    sourceTemplateId,
    isMobile,
    createPreviewElements,
    viewMode,
    customMenuItemsNames,
    enrichedCSM,
    scaleValue,
    customSiteTheme,
    customHomePageName,
    enableVideoPlaying,
  );

  const effectsMapping = getPreviewerEffectsMapping(miniDs, [thumbnailPointer]);
  if (miniDs.utils.isPage(thumbnailPointer)) {
    miniDs.pages.remove(thumbnailPointer.id);
  }
  return (
    <PreviewerItem
      key={thumbnailPointer.id}
      effectsMapping={effectsMapping}
      ThumbnailComponent={thumbnailComponent}
      enablePreviewAnimationsPromise={enablePreviewAnimationsPromise}
      {...previewData}
      onReady={onReady || previewData.onReady}
    />
  );
}

const getDefaultTheme = (editorAPI: Scope['editorAPI']): SiteTheme => ({
  textThemes: editorAPI.theme.textThemes.getAll(),
  colors: editorAPI.theme.colors.getAll() as ColorPalette,
  // TODO fix this
  // @ts-expect-error
  styles: editorAPI.theme.styles.getAll(),
});

const createPreviewWithScope = async (
  { store, editorAPI }: Scope,
  params: CreatePreviewParams,
): Promise<React.ReactElement> => {
  const { ds, createPreviewElements } = store.getMiniSiteManager();

  if (!params.customSiteTheme) {
    params.customSiteTheme = getDefaultTheme(editorAPI);
  }
  return createPreview(ds, createPreviewElements, params);
};

const createPreviewComponentsWithScope = async (
  { store, editorAPI }: Scope,
  params: CreatePreviewComponentsParams,
): Promise<React.ReactElement> => {
  const { ds, createPreviewElements } = store.getMiniSiteManager();

  if (!params.customSiteTheme) {
    params.customSiteTheme = getDefaultTheme(editorAPI);
  }
  return createPreviewComponents(ds, createPreviewElements, params);
};

export const prefetchCustomCSM = async (
  miniDs: DocumentServicesObject,
  publishedTemplateMetasiteId: string,
): Promise<void> => {
  if (!publishedTemplateMetasiteId) return;
  try {
    await miniDs.miniSite.loadCSM(publishedTemplateMetasiteId);
  } catch (e) {
    ErrorReporter.captureException(e, {
      tags: { minisite: true, loadCustomCsm: true },
      extra: { publishedTemplateMetasiteId },
    });
    console.error(e);
  }
};

const prefetchCustomCSMWithScope = async (
  { store }: Scope,
  publishedTemplateMetasiteId: string,
): Promise<void> => {
  const { ds } = store.getMiniSiteManager();
  await prefetchCustomCSM(ds, publishedTemplateMetasiteId);
};

const unstable_createPageSSRComponentsWithScope = async (
  { store }: Scope,
  params: CreatePageSSRComponentsParams,
): Promise<React.ReactElement> => {
  const miniSiteManager = store.getMiniSiteManager();
  return unstable_createPageSSRComponents(miniSiteManager.ds, params);
};

export class PreviewerApi extends BasePublicApi<Scope> {
  prefetchCustomCSM = this.bindScope(prefetchCustomCSMWithScope);
  unstable_createPageSSRComponents = this.bindScope(
    unstable_createPageSSRComponentsWithScope,
  );
  createPreviewComponents = this.bindScope(createPreviewComponentsWithScope);
  createPreview = this.bindScope(createPreviewWithScope);
}
